/**
 * Shared building blocks for the in-app user manuals (Editorial Desk, Subscriber Desk):
 * text styles, the K / Section / H3 / Table / Note / Qa helpers, the Contents rail and the
 * hash scrolling that runs again once web fonts have changed the page height.
 */
import type { ReactNode } from 'react'
import { focus } from './styles'


/** A button, tab or chip name, set so it can be matched to the screen. */
export function K({ children }: { children: ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded-md border border-ink-400 bg-ink-700 px-1.5 py-px font-ui text-[13px] font-semibold text-paper">
      {children}
    </span>
  )
}

export function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="flex scroll-mt-6 flex-col gap-4 border-t border-ink-500 pt-8">
      <h2 id={`${id}-h`} className="m-0 font-serif text-[28px] font-medium leading-tight text-paper">{title}</h2>
      {children}
    </section>
  )
}

export function H3({ children }: { children: ReactNode }) {
  return <h3 className="m-0 mt-2 font-ui text-[16px] font-semibold text-paper">{children}</h3>
}

export function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="max-w-[860px] overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
      <table className="w-full border-collapse text-[14px]">
        <thead>
          <tr>{head.map((h) => <th key={h} scope="col" className="whitespace-nowrap border-b border-ink-500 px-4 py-2.5 text-left text-[12px] font-bold uppercase tracking-[0.06em] text-paper-faint">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="align-top">
              {r.map((c, j) => <td key={j} className={`border-b border-ink-600 px-4 py-2.5 ${j === 0 ? 'font-semibold text-paper' : 'text-paper-dim'}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function Note({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex max-w-[70ch] flex-col gap-1 rounded-r-lg border-l-4 border-amber-glow bg-amber-glow/10 px-4 py-3">
      <p className="m-0 text-[13px] font-bold uppercase tracking-[0.06em] text-amber-deep">{title}</p>
      <p className="m-0 text-[14px] leading-relaxed text-paper">{children}</p>
    </div>
  )
}

export function Qa({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="max-w-[70ch] rounded-lg border border-ink-500 bg-ink-700 px-4 py-3">
      <summary className={`cursor-pointer text-[15px] font-semibold text-paper ${focus}`}>{q}</summary>
      <div className="mt-2 text-[14px] leading-relaxed text-paper-dim">{children}</div>
    </details>
  )
}



export function ContentsRail({ items }: { items: [string, string][] }) {
  return (
    <nav aria-label="Contents" className="flex flex-col gap-0.5 self-start text-[13px] lg:sticky lg:top-6">
      <p className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-paper-faint">Contents</p>
      {items.map(([id, label]) => (
        <a key={id} href={`#${id}`} className={`border-l-2 border-ink-500 py-1 pl-3 text-paper-dim no-underline hover:border-amber-glow hover:text-paper ${focus}`}>
          {label}
        </a>
      ))}
    </nav>
  )
}

